import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { PAGE_BLOCKS, type PageName } from "@avestate/shared";
import { usePageBlocks, useSavePageBlock, type BlocksMap } from "@/hooks/usePageContent.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Textarea } from "@/components/ui/textarea.js";
import { RoleGate } from "@/components/RoleGate.js";
import { Permalink } from "@/components/Permalink.js";
import { pageUrl } from "@/lib/site.js";

const PAGES: { value: PageName; label: string }[] = [
  { value: "home", label: "Home" },
  { value: "contatti", label: "Contatti" },
];

export default function Pages(): JSX.Element {
  const [page, setPage] = useState<PageName>("home");
  const { data, isLoading } = usePageBlocks(page);
  const overrides: BlocksMap = data ?? {};
  const blocks = useMemo(() => PAGE_BLOCKS.filter((b) => b.page === page), [page]);

  return (
    <div className="space-y-[22px]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Pagine</h1>
          <Permalink url={pageUrl(page)} />
          <p className="mt-[10px] text-[13.5px] text-ink-soft">
            Modifica i testi delle pagine pubbliche. Lascia un campo vuoto per usare il testo di default del sito.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-[6px] border border-rule bg-surface p-1">
          {PAGES.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPage(p.value)}
              className={`h-[28px] rounded-[4px] px-3 text-[12.5px] transition-colors ${
                page === p.value ? "bg-accent/10 text-ink" : "text-ink-soft hover:bg-surface-2 hover:text-ink"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      <div className="ticks relative space-y-px rounded-[5px] border border-rule bg-surface">
        {blocks.map((b) => (
          <BlockRow
            key={`${b.page}.${b.key}`}
            page={page}
            blockKey={b.key}
            label={b.label}
            type={b.type}
            value={overrides[b.key]}
          />
        ))}
        {isLoading ? <p className="p-6 text-[13px] text-ink-mute">Carico…</p> : null}
        {blocks.length === 0 ? (
          <p className="p-16 text-center text-ink-mute">Nessun blocco modificabile per questa pagina.</p>
        ) : null}
      </div>
    </div>
  );
}

function BlockRow({
  page,
  blockKey,
  label,
  type,
  value,
}: {
  page: PageName;
  blockKey: string;
  label: string;
  type: "text" | "textarea";
  value: Record<string, string> | undefined;
}): JSX.Element {
  const save = useSavePageBlock();
  const stored = value?.it ?? "";
  const [text, setText] = useState(stored);

  // Re-sync local state when the loaded override changes (page switch / refetch).
  useEffect(() => {
    setText(stored);
  }, [stored]);

  const dirty = text !== stored;
  const inputId = `cms-${page}-${blockKey}`;

  async function onSave() {
    try {
      // Merge with the existing i18n bag so we don't drop en/de.
      await save.mutateAsync({ page, block_key: blockKey, value: { ...(value ?? {}), it: text } });
      toast.success(`Salvato · ${label}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="border-b border-rule-soft p-[16px_18px] last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={inputId} className="micro">{label}</label>
        <span className="font-mono text-[10px] text-ink-mute">{blockKey}</span>
      </div>
      <div className="mt-[8px] flex items-start gap-3">
        <div className="flex-1">
          {type === "textarea" ? (
            <Textarea
              id={inputId}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Usa il testo di default del sito"
            />
          ) : (
            <Input
              id={inputId}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Usa il testo di default del sito"
            />
          )}
        </div>
        <RoleGate min="editor" asDisabled>
          <Button
            type="button"
            size="sm"
            variant={dirty ? "primary" : "ghost"}
            disabled={!dirty || save.isPending}
            onClick={() => void onSave()}
            className="mt-[1px] shrink-0"
          >
            <Check className="size-4" /> {save.isPending ? "Salvo…" : "Salva"}
          </Button>
        </RoleGate>
      </div>
    </div>
  );
}
